import { hasRight } from "@/components/crm/crmShared";
import { useEffect, useMemo, useState } from "react";
import { guardedSave, useLeaveGuard } from "@/lib/leaveRegistry";
import { toast } from "@/lib/toast";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Bell, RefreshCw, Save, Volume2, VolumeX, Play } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { playNotificationBeep } from "@/lib/notificationSound";
import { playAviChime } from "@/lib/aviChime";
import { setSoundPrefs as setSharedSoundPrefs } from "@/lib/soundPrefs";

/**
 * NotifSoundPrefsSection — Paramètres → Son des notifications.
 *
 * Spec rev101 (2026-02) — Toggle global (coupe tout le son de l'app) +
 * activation fine par déclencheur, sur le même modèle que EmailPrefs.
 * Le Premier Admin est le seul à pouvoir modifier ; les autres admins
 * peuvent lire pour comprendre la config.
 */
const CATEGORY_LABELS = {
  tasks:     "Tâches",
  documents: "Documents",
  avi:       "AVI",
  system:    "Système",
};

const CATEGORY_ORDER = ["tasks", "documents", "avi", "system"];

const CATEGORY_ACCENT = {
  tasks:     "#F5C842",
  documents: "#FF8C00",
  avi:       "#EC4899",
  system:    "#0078D4",
};

export default function NotifSoundPrefsSection() {
  const { user } = useAuth();
  const canEdit = hasRight(user, "settings.notification_sounds"); // iter203 — droit de la matrice (plus de premier-admin en dur)

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [values, setValues] = useState({});
  const [initialEnabled, setInitialEnabled] = useState(true);
  const [initialValues, setInitialValues] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/notif-sound-prefs");
      setCatalog(data.catalog || []);
      setEnabled(!!data.enabled);
      setInitialEnabled(!!data.enabled);
      setValues(data.values || {});
      setInitialValues(data.values || {});
      setUpdatedAt(data.updated_at || null);
    } catch (err) {
      toast.error("Chargement impossible", {
        description: err.response?.data?.detail || err.message || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => {
    if (enabled !== initialEnabled) return true;
    return catalog.some((row) => values[row.key] !== initialValues[row.key]);
  }, [enabled, initialEnabled, values, initialValues, catalog]);

  useLeaveGuard({ dirty: !!dirty, label: "Paramètres · Sons des notifications", module: "parametres", save: guardedSave(() => save()) }); // eslint-disable-line no-use-before-define
  const toggle = (key, next) => setValues((v) => ({ ...v, [key]: next }));

  const save = async () => {
    setSaving(true);
    try {
      const updates = {};
      catalog.forEach((row) => { updates[row.key] = !!values[row.key]; });
      const { data } = await api.put("/settings/notif-sound-prefs", {
        enabled,
        values: updates,
      });
      setEnabled(!!data.enabled);
      setInitialEnabled(!!data.enabled);
      setValues(data.values || {});
      setInitialValues(data.values || {});
      setUpdatedAt(data.updated_at || null);
      setSharedSoundPrefs(data);
      toast.success("Préférences son enregistrées");
    } catch (err) {
      toast.error("Enregistrement impossible", {
        description: err.response?.data?.detail || err.message || "",
      });
    } finally {
      setSaving(false);
    }
  };

  const grouped = useMemo(() => {
    const buckets = {};
    catalog.forEach((row) => {
      const c = row.category || "other";
      if (!buckets[c]) buckets[c] = [];
      buckets[c].push(row);
    });
    return buckets;
  }, [catalog]);

  const activeCount = useMemo(() => {
    if (!enabled) return 0;
    return catalog.filter((r) => values[r.key]).length;
  }, [enabled, catalog, values]);

  if (loading) {
    return (
      <Card className="bg-card border-vertec-borderDark p-8">
        <div className="text-sm text-muted-foreground">Chargement des préférences son…</div>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-vertec-borderDark p-8" data-testid="notif-sound-prefs-section">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-start gap-3">
          <div
            className="rounded-lg flex items-center justify-center shrink-0"
            style={{ width: 44, height: 44, background: "#141417", border: "1px solid #26262C", color: "#F5C842" }}
          >
            <Bell size={22} />
          </div>
          <div>
            <h2 className="text-2xl font-semibold mb-1">Son des notifications</h2>
            <p className="text-muted-foreground text-sm max-w-2xl">
              Contrôlez le son émis par la cloche 🔔 à l'arrivée d'une nouvelle notification,
              ainsi que les carillons AVI temps réel (nouvelle demande, décision, message).
              Chaque déclencheur peut être activé/désactivé indépendamment. Le toggle global
              en haut coupe le son pour toute l'application, quels que soient les réglages fins.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
            style={{
              background: "transparent",
              color: enabled ? "#22C55E" : "#EF4444",
              border: `1px solid ${enabled ? "rgba(34,197,94,0.45)" : "rgba(239,68,68,0.45)"}`,
            }}
            data-testid="notif-sound-active-count"
          >
            {enabled ? <Volume2 size={13} /> : <VolumeX size={13} />}
            <span>{enabled ? `${activeCount} / ${catalog.length} sons actifs` : "Son coupé globalement"}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => playNotificationBeep()}
            className="gap-1.5 border-vertec-borderDark"
            data-testid="notif-sound-preview"
            title="Écouter le son de la cloche"
          >
            <Play size={13} /> Écouter
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={saving}
            className="gap-1.5 border-vertec-borderDark"
            data-testid="notif-sound-reload"
          >
            <RefreshCw size={13} /> Actualiser
          </Button>
        </div>
      </div>

      {/* Global toggle */}
      <div
        className="flex items-center gap-4 rounded-lg p-4 mb-6"
        style={{
          background: "#17171B",
          border: "1px solid #26262C",
          borderLeft: `3px solid ${enabled ? "#F5C842" : "#EF4444"}`,
        }}
        data-testid="notif-sound-global-row"
      >
        <Switch
          checked={enabled}
          onCheckedChange={setEnabled}
          disabled={!canEdit || saving}
          data-testid="notif-sound-global-switch"
        />
        <div className="flex-1">
          <div className="text-sm font-semibold flex items-center gap-2">
            {enabled ? <Volume2 size={14} style={{ color: "#F5C842" }} /> : <VolumeX size={14} style={{ color: "#EF4444" }} />}
            Son global de la cloche
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Coupe le son pour <strong>toute l'application</strong>, même si des déclencheurs individuels ci-dessous restent actifs.
          </div>
        </div>
      </div>

      {/* Groups */}
      <div className="space-y-8" style={{ opacity: enabled ? 1 : 0.55 }}>
        {CATEGORY_ORDER.filter((c) => grouped[c]).map((cat) => {
          const rows = grouped[cat];
          const accent = CATEGORY_ACCENT[cat] || "#6b7280";
          return (
            <div key={cat} data-testid={`notif-sound-category-${cat}`}>
              <div className="flex items-center gap-2 mb-3">
                <div style={{ width: 3, height: 20, background: accent, borderRadius: 2 }} />
                <h3 className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: accent }}>
                  {CATEGORY_LABELS[cat] || cat}
                </h3>
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  · {rows.length} déclencheur{rows.length > 1 ? "s" : ""}
                </span>
                {cat === "avi" && (
                  <button
                    type="button"
                    onClick={() => playAviChime({ force: true })}
                    className="ml-auto inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border transition-colors hover:bg-white/5"
                    style={{ color: accent, borderColor: "rgba(236,72,153,0.35)" }}
                    data-testid="notif-sound-preview-avi"
                    title="Écouter le carillon AVI"
                  >
                    <Play size={11} /> Écouter le carillon AVI
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {rows.map((row) => {
                  const on = !!values[row.key];
                  return (
                    <div
                      key={row.key}
                      className="flex items-start gap-4 rounded-md p-4"
                      style={{
                        background: "#17171B",
                        border: "1px solid #26262C",
                      }}
                      data-testid={`notif-sound-row-${row.key}`}
                    >
                      <div className="pt-0.5">
                        <Switch
                          checked={on}
                          onCheckedChange={(v) => toggle(row.key, v)}
                          disabled={!canEdit || saving || !enabled}
                          data-testid={`notif-sound-switch-${row.key}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm font-semibold">{row.label}</div>
                          {!on && enabled && (
                            <span
                              className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded"
                              style={{ background: "transparent", border: "1px solid rgba(239,68,68,0.45)", color: "#EF4444" }}
                            >
                              Muet
                            </span>
                          )}
                          {on && enabled && (
                            <span
                              className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded"
                              style={{ background: "transparent", border: "1px solid rgba(34,197,94,0.45)", color: "#22C55E" }}
                            >
                              Audible
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-8 pt-6 border-t border-vertec-borderDark flex items-center justify-between gap-4 flex-wrap">
        <div className="text-xs text-muted-foreground">
          {updatedAt ? (
            <>Dernière mise à jour : {new Date(updatedAt).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" })}</>
          ) : (
            <>Aucune modification enregistrée à ce jour — tous les sons sont actifs par défaut.</>
          )}
        </div>
        {canEdit ? (
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="gap-1.5 bg-[#0078D4] hover:bg-[#005ea6] text-white"
            data-testid="notif-sound-save"
          >
            <Save size={14} /> {saving ? "Enregistrement…" : dirty ? "Enregistrer les modifications" : "Enregistré"}
          </Button>
        ) : (
          <div className="text-xs text-muted-foreground italic">
            Seul le Premier Admin peut modifier ces préférences.
          </div>
        )}
      </div>
    </Card>
  );
}
